// Phase 1A · Step 26 — BriefShell styles.
//
// Token-only styles for The Brief's per-mode shell. Sits inside the
// global <ModeShell> main container (which already constrains The
// Brief to max-width 640px per Step 25). BriefShell re-asserts that
// 640px constraint defensively so the dossier framing stays correct
// even if ModeShell's main container changes.
//
// No hex literals, no icon library, no emoji, no drop shadows. All
// values reference --sz-* tokens. UI-architecture §5 governs The
// Brief's spatial logic; full §5 polish (sticky action bar, italic
// 300-weight DM Sans for agent reasoning) is out of scope for Step 26
// — this step migrates the existing Phantom panels into the shell
// without redesigning their internals.

import type { CSSProperties } from "react";

// Outer article — the dossier frame. Centered single column.
export const article: CSSProperties = {
  width: "100%",
  maxWidth: "640px",
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-6)",
};

// Header block: eyebrow + title + description + sub-nav.
export const header: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-3)",
  paddingBottom: "var(--sz-space-4)",
  borderBottom: "1px solid var(--sz-border-subtle)",
};

export const eyebrow: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-amber-text)",
};

export const title: CSSProperties = {
  fontFamily: "var(--sz-font-head)",
  fontSize: "var(--sz-head-md)",
  fontWeight: 700,
  color: "var(--sz-text-primary)",
  lineHeight: 1.2,
};

export const description: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-md)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.6,
};

// Brief sub-navigation: minimal two-link row between Current / History.
// No tab system, no state — just NavLinks. Active state uses the
// amber-text token (text on dark) plus an amber underline.
export const nav: CSSProperties = {
  display: "flex",
  gap: "var(--sz-space-5)",
  marginTop: "var(--sz-space-2)",
};

const navItemBase: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  textDecoration: "none",
  paddingBottom: "4px",
  borderBottom: "1px solid transparent",
};

export const navItem: CSSProperties = {
  ...navItemBase,
  color: "var(--sz-text-muted)",
};

export const navItemActive: CSSProperties = {
  ...navItemBase,
  color: "var(--sz-amber-text)",
  borderBottomColor: "var(--sz-amber)",
};

// Body slot — children area. Vertical stack with gap so multiple
// children (e.g. PhantomSlipPanel + future agent reasoning panel) sit
// cleanly on top of each other.
export const body: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-5)",
};

// Dashboard back-link (Phase 1A Step 28). Quiet secondary link so users
// who land at /brief by default can still reach the temporary Phase 1A
// dashboard without typing the URL. Muted styling on purpose — this is a
// migration-window affordance, not a permanent navigation primitive.
//
// Phase 1A Step 39 — a sibling /home link uses the same `dashboardLink`
// style. Both links sit inside the new `backLinkRow` flex container so
// they line up horizontally without disturbing The Brief's dossier
// framing.
export const dashboardLink: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  textDecoration: "none",
  borderBottom: "1px solid var(--sz-border-default)",
  paddingBottom: "2px",
  alignSelf: "flex-start",
};

// Container for the Brief header's quiet back-link row. Houses the
// /home link plus the existing /dashboard link side-by-side. No new
// nav semantics — same muted styling as the original single link.
export const backLinkRow: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: "var(--sz-space-4)",
  alignSelf: "flex-start",
};
